import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { get } from '../api.js';
import { useOffline } from '../offline/context.js';

/**
 * Linked to (brief section 7.7), as docs/prototype.html draws it (.lp): a
 * search over the portal's companies, contacts and leads, at most 10
 * items, filtered as you type, "Showing 10 of N. Keep typing to narrow."
 * HubSpot records join the same list at step 4 (the server merges them).
 * Without network, the records this device keeps. Inside a window the list
 * sits in the page, so it never scrolls on its own.
 */
export type LinkKind = 'company' | 'contact' | 'deal' | 'lead';

export interface LinkItem {
  kind: LinkKind;
  /** The portal record's ID; null for a company typed on contacts and leads. */
  id: string | null;
  label: string;
  sub: string | null;
  company: string | null;
  hubspotId?: string | null;
  /** Where it comes from: the portal, or HubSpot (step 4). */
  source?: 'portal' | 'hubspot';
}

/** What a search answers: the portal's records, then HubSpot's (step 4). */
export interface SearchResult {
  items: LinkItem[];
  total: number;
  /** HubSpot did not answer: the portal's records alone. */
  hubspot?: 'unavailable';
}

export const LINK_KIND_LABELS: Record<LinkKind, string> = {
  company: 'Company',
  contact: 'Contact',
  deal: 'Deal',
  lead: 'Lead',
};

const MAX = 10;

export function LinkPicker({
  label,
  value,
  onChange,
  kinds = ['company', 'contact', 'lead'],
  mark,
  error,
  hint = 'Search the portal',
  allowTyped = false,
}: {
  label: string;
  value: LinkItem | null;
  onChange(value: LinkItem | null): void;
  kinds?: LinkKind[];
  mark?: boolean;
  error?: string;
  hint?: string;
  /** Offers the text as typed as a company name (a company not in the portal or HubSpot yet, brief section 3). */
  allowTyped?: boolean;
}) {
  const id = useId();
  const { store } = useOffline();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const kindsKey = kinds.join(',');

  useEffect(() => {
    const q = term.trim();
    if (!q) return;
    let current = true;
    const timer = setTimeout(() => {
      void searchRecords(q, kindsKey.split(',') as LinkKind[], store).then(
        (r) => current && setResult(r),
      );
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [term, kindsKey, store]);

  const shown = term.trim() ? result : null;

  return (
    <div className="lp">
      <label className="f" htmlFor={`${id}-q`}>
        <span>
          {label}
          {mark && (
            <>
              {' '}
              <span className="req" aria-hidden="true">
                *
              </span>
            </>
          )}
        </span>
        <small>{hint}</small>
      </label>
      {value ? (
        <div className="lp-sel">
          <div className="lp-chip">
            <span className={`lp-k ${value.kind}`}>{LINK_KIND_LABELS[value.kind]}</span>
            <span>
              <span className="tt">{value.label}</span>
              <small>{value.sub ?? value.company ?? ''}</small>
            </span>
            <button
              type="button"
              aria-label={`Remove ${value.label}`}
              onClick={() => {
                onChange(null);
                setTimeout(() => input.current?.focus());
              }}
            >
              ×
            </button>
          </div>
        </div>
      ) : (
        <input
          ref={input}
          id={`${id}-q`}
          type="search"
          placeholder="Company, contact or deal"
          autoComplete="off"
          style={{ marginTop: 6 }}
          value={term}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-err` : undefined}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        />
      )}
      {!value && open && (
        <div className="lp-drop" role="listbox" aria-label={`${label} suggestions`}>
          {!term.trim() ? (
            <div className="lp-msg">Type to search.</div>
          ) : !shown ? (
            <div className="lp-msg">Searching…</div>
          ) : shown.items.length === 0 && !allowTyped ? (
            <div className="lp-msg">Nothing found for “{term.trim()}”.</div>
          ) : (
            <>
              {shown.items.map((item, i) => (
                <Fragment key={`${item.kind}:${item.id ?? item.hubspotId ?? item.label}`}>
                  {item.source === 'hubspot' && shown.items[i - 1]?.source !== 'hubspot' && (
                    <div className="lp-h">In HubSpot</div>
                  )}
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="lp-it"
                    onClick={() => {
                      onChange(item);
                      setOpen(false);
                      setTerm('');
                    }}
                  >
                    <span className={`lp-k ${item.kind}`}>{LINK_KIND_LABELS[item.kind]}</span>
                    <span>
                      <span className="tt">{item.label}</span>
                      <small>{item.sub ?? ''}</small>
                    </span>
                  </button>
                </Fragment>
              ))}
              {allowTyped &&
                !shown.items.some(
                  (i) =>
                    i.kind === 'company' && i.label.toLowerCase() === term.trim().toLowerCase(),
                ) && (
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="lp-it"
                    onClick={() => {
                      const name = term.trim();
                      onChange({
                        kind: 'company',
                        id: null,
                        label: name,
                        sub: 'Company name as typed',
                        company: name,
                      });
                      setOpen(false);
                      setTerm('');
                    }}
                  >
                    <span className="lp-k company">{LINK_KIND_LABELS.company}</span>
                    <span>
                      <span className="tt">Use “{term.trim()}” as the company name</span>
                      <small>A company not in the portal yet</small>
                    </span>
                  </button>
                )}
              <div className="lp-foot">
                {shown.total > shown.items.length
                  ? `Showing ${shown.items.length} of ${shown.total}. Keep typing to narrow.`
                  : shown.total === 0
                    ? `Nothing found for “${term.trim()}”.`
                    : `${shown.total} match${shown.total > 1 ? 'es' : ''}.`}
                {shown.hubspot === 'unavailable' && ' HubSpot is not answering: the portal only.'}
              </div>
            </>
          )}
        </div>
      )}
      {error && (
        <small className="err" id={`${id}-err`}>
          {error}
        </small>
      )}
    </div>
  );
}

/** The portal's records matching what is typed (at most 10); without network, this device's. */
export async function searchRecords(
  q: string,
  kinds: LinkKind[],
  store: ReturnType<typeof useOffline>['store'],
): Promise<SearchResult | null> {
  return get<SearchResult>(
    `/people/search?q=${encodeURIComponent(q)}&kinds=${kinds.join(',')}&limit=${MAX}`,
  ).catch(async () => (store ? searchDevice(store, q, kinds) : null));
}

/** Without network: the companies, contacts and leads this device keeps. */
async function searchDevice(
  store: NonNullable<ReturnType<typeof useOffline>['store']>,
  q: string,
  kinds: LinkKind[],
): Promise<{ items: LinkItem[]; total: number }> {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (text: string) => words.every((w) => text.toLowerCase().includes(w));
  const [contacts, leads] = await Promise.all([
    store.listRecords('contact'),
    store.listRecords('lead'),
  ]);
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const items: LinkItem[] = [];
  if (kinds.includes('company')) {
    const names = new Map<string, string>();
    for (const r of [
      ...contacts.map((c) => str(c.company)),
      ...leads.map((l) => str(l.company_name)),
    ]) {
      if (r && hit(r) && !names.has(r.toLowerCase())) names.set(r.toLowerCase(), r);
    }
    for (const name of [...names.values()].sort()) {
      items.push({ kind: 'company', id: null, label: name, sub: null, company: name });
    }
  }
  if (kinds.includes('contact')) {
    for (const c of contacts) {
      if (c.deleted) continue;
      const name = [str(c.first_name), str(c.last_name)].filter(Boolean).join(' ') || str(c.email);
      if (!hit(`${name} ${str(c.company)} ${str(c.email)}`)) continue;
      items.push({
        kind: 'contact',
        id: c.id,
        label: name || 'Contact',
        sub: [str(c.job_title), str(c.company)].filter(Boolean).join(', ') || null,
        company: str(c.company) || null,
      });
    }
  }
  if (kinds.includes('lead')) {
    for (const l of leads) {
      if (l.deleted) continue;
      const label = str(l.deal_name) || str(l.company_name) || 'Lead';
      if (!hit(`${label} ${str(l.company_name)}`)) continue;
      items.push({
        kind: 'lead',
        id: l.id,
        label,
        sub: str(l.company_name) || null,
        company: str(l.company_name) || null,
      });
    }
  }
  return { items: items.slice(0, MAX), total: items.length };
}
